import Call from "@/script/call";

const CH_CREATE = "douDianBatchOperationProduct.createMarketingCoupon";
const SHOP_CONCURRENCY = 4;

function toSec(ms) {
    return Math.floor(Number(ms) / 1000);
}

function failReasonOf(rs, fallback) {
    return (rs && (rs.msg || rs.message)) || fallback || "创建失败";
}

async function asyncPool(limit, items, iterator) {
    const executing = new Set();
    for (const item of items) {
        const p = Promise.resolve()
            .then(() => iterator(item))
            .catch((e) => console.error("[优惠券] 并发任务异常", e));
        executing.add(p);
        p.finally(() => executing.delete(p));
        if (executing.size >= limit) {
            await Promise.race(executing);
        }
    }
    await Promise.all(executing);
}

/** 由表单拼装 createMarketingCoupon 请求体（不含 coupon_name，由 thread 按 title_mode 拼接） */
export function buildYouhuiquanOpts(form, { startApplySec, endApplySec, useStartSec, useEndSec, goods }) {
    const f = form || {};
    const channelKey = String(f.channel_key || "public");
    const goodsScope = Number(f.goods_scope) === 1 ? 1 : 2;
    const discountKind = String(f.discount_kind || "reduce");
    const unlimitedStock = f.stock_mode !== "limited";
    const unlimitedUser = f.user_limit_mode !== "limited";
    const opts = {
        channelKey,
        goodsScope,
        discountKind,
        title_mode: f.title_mode || "default",
        title_prefix: f.title_prefix || "",
        start_apply_time: startApplySec,
        end_apply_time: endApplySec,
        renew_switch_on: !!f.renew_switch_on,
        unlimited_stock: unlimitedStock,
        total_amount: unlimitedStock ? 1000000 : Math.max(1, Number(f.total_amount) || 10000),
        unlimited_user_apply_number: unlimitedUser,
        max_apply_times: unlimitedUser ? 100 : Math.max(1, Number(f.max_apply_times) || 3),
        goods: goodsScope === 1 ? goods || [] : [],
        ignore: false,
    };

    if (discountKind === "threshold") {
        opts.threshold = Math.round(Number(f.threshold_yuan) * 100);
        opts.credit = Math.round(Number(f.credit_yuan) * 100);
    } else if (discountKind === "discount") {
        opts.discountZhe = Number(f.discount_zhe);
    } else {
        opts.credit = Math.round(Number(f.credit_yuan) * 100);
    }

    if (channelKey === "live") {
        opts.period_type = 2;
        opts.live_coupon_duration = Math.max(1, Number(f.live_coupon_duration) || 30);
        opts.valid_period_second = Math.max(60, Math.round(Number(f.live_valid_minutes) || 30) * 60);
    } else if (f.period_mode === "days") {
        opts.period_type = 2;
        opts.valid_period = String(Math.max(1, Number(f.valid_days) || 7));
    } else {
        opts.period_type = 1;
        if (f.period_mode === "custom" && useStartSec && useEndSec) {
            opts.start_time = useStartSec;
            opts.expire_time = useEndSec;
        } else {
            opts.start_time = startApplySec;
            opts.expire_time = endApplySec;
        }
    }
    return opts;
}

/**
 * 多店并行创建优惠券。
 * 全店：每店 1 张；指定商品：按店汇总勾选商品，每店 1 张（goods 为该店商品 id 列表）。
 */
export async function runYouhuiquanCreate({ state, commit, shopList }) {
    const form = state.yhq_form;
    const applyRange = state.yhq_apply_range || [];
    const useRange = state.yhq_use_range || [];
    const startApplySec = toSec(applyRange[0]);
    const endApplySec = toSec(applyRange[1]);
    const useStartSec = useRange[0] ? toSec(useRange[0]) : startApplySec;
    const useEndSec = useRange[1] ? toSec(useRange[1]) : endApplySec;
    const stopRequested = () => state.yhq_create_stop_requested;
    const shopOf = (id) =>
        (shopList || []).find((s) => String(s.id) === String(id)) || null;

    const goodsScope = Number(form.goods_scope) === 1 ? 1 : 2;
    let jobs = [];

    if (goodsScope === 2) {
        jobs = (state.yhq_selected_shop_ids || [])
            .map((id) => {
                const shop = shopOf(id);
                if (!shop) return null;
                return { shop, goods: [] };
            })
            .filter(Boolean);
    } else {
        const sel = new Set(state.yhq_product_selects || []);
        const byShop = new Map();
        for (const row of state.yhq_products || []) {
            if (!sel.has(row._key)) continue;
            const sid = String(row.shop_id);
            if (!byShop.has(sid)) byShop.set(sid, []);
            byShop.get(sid).push(String(row.product_id));
        }
        for (const [sid, goods] of byShop) {
            const shop = shopOf(sid);
            if (!shop || !goods.length) continue;
            jobs.push({ shop, goods });
        }
    }

    if (!jobs.length) return;
    commit("yhqCreateStart", jobs.length);

    await asyncPool(SHOP_CONCURRENCY, jobs, async (job) => {
        if (stopRequested()) {
            commit("yhqCreateResultPush", [
                {
                    ok: false,
                    shop_name: job.shop.shop_name || job.shop.id,
                    title: goodsScope === 2 ? "全店商品" : `指定商品×${job.goods.length}`,
                    message: "用户已停止创建",
                },
            ]);
            commit("yhqCreateProgress", { done: 1, fail: 1 });
            return;
        }
        const shopName = job.shop.shop_name || job.shop.id;
        const opts = buildYouhuiquanOpts(form, {
            startApplySec,
            endApplySec,
            useStartSec,
            useEndSec,
            goods: job.goods,
        });
        const rs = await Call.invoke(CH_CREATE, job.shop, opts);
        const ok = rs && rs.code == 1;
        const metaId =
            ok && rs.data && (rs.data.coupon_meta_id || rs.data.couponMetaId)
                ? String(rs.data.coupon_meta_id || rs.data.couponMetaId)
                : "";
        commit("yhqCreateResultPush", [
            {
                ok,
                shop_name: shopName,
                title: goodsScope === 2 ? "全店商品" : `指定商品×${job.goods.length}`,
                message: ok
                    ? `创建成功${metaId ? `，券ID：${metaId}` : ""}`
                    : failReasonOf(rs),
            },
        ]);
        commit("yhqCreateProgress", {
            done: 1,
            success: ok ? 1 : 0,
            fail: ok ? 0 : 1,
        });
    });
}
